import React from 'react';
import axios from 'axios';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import {
  API_URL_TENDERS_EXPORT,
  API_URL_TENDERS,
  API_URL_TENDER_DEPARTMENT_STATUS,
  tenderColumnsDefinitionOverview,
  API_URL_LANES_PRICING,
  API_BASE_URL,
} from '../configuration/Configuration';
import {
  isPosted,
  headerColumns,
  extractFilenameFromContentDisposition,
  exportDepartmentStatusToExcel,
} from '../configuration/Utils';
import { useAuth } from '../context/AuthContext';
import { AgGridReact } from 'ag-grid-react';
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import TableControls from '../integrations/core/table/TableControls';
import {
  applySavedColumnLayout,
  getColumnLayoutScope,
  saveGridColumnLayout,
} from '../integrations/core/table/columnLayoutPersistence';
import { FullScreen, useFullScreenHandle } from 'react-full-screen';

const EXPORT_PAGE_SIZE = 14;

const Export = () => {
  window.addEventListener('beforeunload', () => {
    setTender([]);
  });

  const { key } = useLocation();
  const navigate = useNavigate();

  const { isAuth, userType, user } = useAuth();

  useEffect(() => {
    reset();
    getTenderDataOverview();
  }, [key]);

  const getTenderDataOverview = () => {
    (async () => {
      const opt = [];
      const url_api = API_URL_TENDERS;
      const { data } = await axios.get(url_api);
      data.forEach((value) => {
        opt.push(value);
      });
      setTenderData(opt);
    })();
  };

  const [tenders, setTender] = React.useState('');
  const [tenderData, setTenderData] = React.useState([]);
  const [tenderData2, setTenderData2] = React.useState([]);
  const overviewLayoutScope = getColumnLayoutScope();
  const detailLayoutScope = getColumnLayoutScope(tenders);
  const persistColumnLayout = React.useCallback((event, layoutScope) => {
    saveGridColumnLayout(event.columnApi, layoutScope);
  }, []);
  const persistColumnLayoutOnResize = React.useCallback(
    (event, layoutScope) => {
      if (!event.finished) {
        return;
      }

      saveGridColumnLayout(event.columnApi, layoutScope);
    },
    []
  );

  const loadTender = (mode) => () => {
    (async () => {
      // Get the current column order from the AG Grid table
      let columnOrder = [];
      if (gridRef2.current && gridRef2.current.columnApi) {
        const allColumns = gridRef2.current.columnApi.getAllDisplayedColumns();
        columnOrder = allColumns.map((col) => col.getColId());
      }

      const url_api_file =
        API_URL_TENDERS_EXPORT +
        (user || '') +
        '/' +
        tenders +
        '/' +
        mode +
        '/';

      try {
        const response = await axios.post(
          url_api_file,
          { columnOrder },
          {
            responseType: 'blob',
          }
        );
        const contentDisposition = response.headers['content-disposition'];

        // Better filename extraction that handles quoted filenames
        const filename =
          extractFilenameFromContentDisposition(contentDisposition);

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
      } catch (error) {
        console.error('Error downloading file:', error);
      }
    })();
  };

  const reset = () => {
    setTenderSelectState(false);
    setDeptStatusOpen(false);
    setDeptStatusRows([]);
    setDeptStatusError('');
  };

  // grid:
  const gridRef1 = React.useRef();
  const gridRef2 = React.useRef();
  const [tendersSelectionState, setTenderSelectState] = React.useState(false);
  const onTenderSelected = (params) => {
    setTender(params.data.id);
    getTenderData(params.data.id);
    setTenderSelectState(true);
  };

  // Department status popup
  const [deptStatusOpen, setDeptStatusOpen] = React.useState(false);
  const [deptStatusRows, setDeptStatusRows] = React.useState([]);
  const [deptStatusLoading, setDeptStatusLoading] = React.useState(false);
  const [deptStatusError, setDeptStatusError] = React.useState('');

  const handleOpenDeptStatus = async () => {
    if (!tenders) return;
    setDeptStatusOpen(true);
    setDeptStatusLoading(true);
    setDeptStatusError('');
    try {
      const res = await axios.get(API_URL_TENDER_DEPARTMENT_STATUS(tenders), {
        withCredentials: true,
      });
      setDeptStatusRows(res.data || []);
    } catch (err) {
      setDeptStatusError(
        err?.response?.data?.error ||
          err?.message ||
          'Could not load department status.'
      );
    } finally {
      setDeptStatusLoading(false);
    }
  };

  const [gridAdmin, setGridAdmin] = React.useState();
  const [company, setCompany] = React.useState('');

  const getTenderData = (tender) => {
    (async () => {
      const url_api = API_URL_LANES_PRICING + tender + '/admin';
      const { data } = await axios.get(url_api);
      setTenderData2(data.rows);
      setCompany(data.tender[0].company);
      const laneColumnDefinitionAdmin = await headerColumns(tender, true);
      setGridAdmin(laneColumnDefinitionAdmin);
    })();
  };
  const fullScreenHandle = useFullScreenHandle();

  return (
    <Box sx={{ flexGrow: 1, minHeight: '100svh' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Typography variant="h6" align="left">
          Export Tender
        </Typography>
        {isAuth && isPosted(tendersSelectionState) && (
          <>
            {userType && (userType.isAdmin || userType.isStaff) && (
              <>
                <Button
                  variant="outlined"
                  size="medium"
                  color="inherit"
                  onClick={reset}
                  sx={{ alignSelf: 'start' }}
                >
                  {`← Back`}
                </Button>

                <Typography fontWeight={600} align="left">
                  {company} tender with ID {tenders} Selected
                </Typography>
              </>
            )}
          </>
        )}
        <FullScreen handle={fullScreenHandle}>
          {isAuth && (
            <>
              {userType && (userType.isAdmin || userType.isStaff) && (
                <div
                  style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
                >
                  {isPosted(!tendersSelectionState) && (
                    <div className="ag-theme-alpine">
                      <TableControls
                        table={gridRef1}
                        fullScreenHandle={fullScreenHandle}
                        layoutScope={overviewLayoutScope}
                      />

                      <AgGridReact
                        ref={gridRef1} // Ref for accessing Grid's API
                        rowData={tenderData} // Row Data for Rows
                        columnDefs={applySavedColumnLayout(
                          tenderColumnsDefinitionOverview,
                          overviewLayoutScope
                        )} // Column Defs for Columns
                        onColumnMoved={(event) =>
                          persistColumnLayout(event, overviewLayoutScope)
                        }
                        onColumnPinned={(event) =>
                          persistColumnLayout(event, overviewLayoutScope)
                        }
                        onColumnVisible={(event) =>
                          persistColumnLayout(event, overviewLayoutScope)
                        }
                        onColumnResized={(event) =>
                          persistColumnLayoutOnResize(
                            event,
                            overviewLayoutScope
                          )
                        }
                        onRowDoubleClicked={onTenderSelected}
                        pagination={true}
                        paginationPageSize={EXPORT_PAGE_SIZE}
                        domLayout="autoHeight"
                      />
                    </div>
                  )}
                  {isPosted(tendersSelectionState) && (
                    <div className="ag-theme-alpine">
                      <TableControls
                        showExport={true}
                        table={gridRef2}
                        tenders={tenders}
                        user={user}
                        fullScreenHandle={fullScreenHandle}
                        layoutScope={detailLayoutScope}
                      />

                      <AgGridReact
                        ref={gridRef2} // Ref for accessing Grid's API
                        rowData={tenderData2} // Row Data for Rows
                        columnDefs={gridAdmin} // Column Defs for Columns
                        onColumnMoved={(event) =>
                          persistColumnLayout(event, detailLayoutScope)
                        }
                        onColumnPinned={(event) =>
                          persistColumnLayout(event, detailLayoutScope)
                        }
                        onColumnVisible={(event) =>
                          persistColumnLayout(event, detailLayoutScope)
                        }
                        onColumnResized={(event) =>
                          persistColumnLayoutOnResize(event, detailLayoutScope)
                        }
                        pagination={true}
                        paginationPageSize={EXPORT_PAGE_SIZE}
                        domLayout="autoHeight"
                      />
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {isPosted(tendersSelectionState) && (
            <>
              {userType && (userType.isAdmin || userType.isStaff) && (
                <>
                  <Typography align="left" style={{ marginTop: 16 }}>
                    Select a full export or a customer export without internal
                    information.
                  </Typography>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                      marginTop: 16,
                    }}
                  >
                    <Button
                      variant="outlined"
                      size="medium"
                      onClick={loadTender('full')}
                      color="inherit"
                    >
                      Full Export
                    </Button>

                    <Button
                      variant="outlined"
                      size="medium"
                      onClick={loadTender('customer')}
                      color="inherit"
                    >
                      Customer Export
                    </Button>

                    <Button
                      variant="outlined"
                      size="medium"
                      color="primary"
                      onClick={() => navigate(`/Import?id=${tenders}`)}
                    >
                      Import Lanes
                    </Button>

                    <Button
                      variant="outlined"
                      size="medium"
                      onClick={handleOpenDeptStatus}
                    >
                      Show Tender Info
                    </Button>
                  </div>
                </>
              )}
            </>
          )}

          {/* Department Status Popup */}
          <Dialog
            open={deptStatusOpen}
            onClose={() => setDeptStatusOpen(false)}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>Department Status</DialogTitle>
            <DialogContent>
              {deptStatusLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              )}
              {deptStatusError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {deptStatusError}
                </Alert>
              )}
              {!deptStatusLoading &&
                !deptStatusError &&
                deptStatusRows.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    No departments have been assigned to this tender yet.
                  </Typography>
                )}
              {!deptStatusLoading && deptStatusRows.length > 0 && (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <strong>Department</strong>
                        </TableCell>
                        <TableCell align="center">
                          <strong>Viewed</strong>
                        </TableCell>
                        <TableCell align="center">
                          <strong>Declined</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Decline Reason</strong>
                        </TableCell>
                        <TableCell align="center">
                          <strong>Document Uploaded</strong>
                        </TableCell>
                        <TableCell align="center">
                          <strong>Prices Submitted</strong>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {deptStatusRows.map((row) => (
                        <TableRow key={row.department}>
                          <TableCell>{row.department}</TableCell>
                          <TableCell align="center">
                            {row.viewed ? (
                              <CheckCircleOutlineIcon
                                color="success"
                                fontSize="small"
                              />
                            ) : (
                              <RemoveCircleOutlineIcon
                                color="disabled"
                                fontSize="small"
                              />
                            )}
                          </TableCell>
                          <TableCell align="center">
                            {row.declined ? (
                              <CheckCircleOutlineIcon
                                color="error"
                                fontSize="small"
                              />
                            ) : (
                              <RemoveCircleOutlineIcon
                                color="disabled"
                                fontSize="small"
                              />
                            )}
                          </TableCell>
                          <TableCell
                            sx={{
                              maxWidth: 250,
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                            }}
                          >
                            {row.declined ? row.decline_reason : '—'}
                          </TableCell>
                          <TableCell align="center">
                            {row.document_uploaded ? (
                              <CheckCircleOutlineIcon
                                color="success"
                                fontSize="small"
                              />
                            ) : (
                              <RemoveCircleOutlineIcon
                                color="disabled"
                                fontSize="small"
                              />
                            )}
                          </TableCell>
                          <TableCell align="center">
                            {row.prices_submitted ? (
                              <CheckCircleOutlineIcon
                                color="success"
                                fontSize="small"
                              />
                            ) : (
                              <RemoveCircleOutlineIcon
                                color="disabled"
                                fontSize="small"
                              />
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </DialogContent>
            <DialogActions>
              {deptStatusRows.length > 0 && (
                <Button
                  onClick={() => exportDepartmentStatusToExcel(tenders, deptStatusRows)}
                >
                  Export
                </Button>
              )}
              <Button onClick={() => setDeptStatusOpen(false)}>Close</Button>
            </DialogActions>
          </Dialog>
          {userType && !userType.isAdmin && !userType.isStaff && (
            <Typography>Tender Management only!</Typography>
          )}
        </FullScreen>
      </div>
    </Box>
  );
};
export default Export;
